import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtTimeRange } from "@/lib/format";
import { CheckCircle2, LogIn, LogOut, Sparkles, Loader2, XCircle } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function CheckinPage() {
  const { rehearsalId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const { user, selectProduction } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "idle", data: null, error: "" });
  const [action, setAction] = useState("in"); // "in" | "out"

  const submit = async (which) => {
    setAction(which);
    setState({ status: "loading", data: null, error: "" });
    try {
      const { data } = await api.post(`/rehearsals/${rehearsalId}/checkin`, {
        token,
        action: which,
      });
      setState({ status: "success", data, error: "" });
    } catch (e) {
      setState({ status: "error", data: null, error: e.response?.data?.detail || "Check-in failed" });
    }
  };

  useEffect(() => {
    // If not authed, bounce to login and preserve return URL.
    if (user === false) {
      const returnTo = `/checkin/${rehearsalId}?t=${encodeURIComponent(token)}`;
      nav(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
      return;
    }
    // Once authed and token present, auto-fire the default action = "in".
    if (user && user.role && token && state.status === "idle") {
      submit("in");
    }
  }, [user, token, state.status, rehearsalId, nav]);

  if (!token) {
    return (
      <Wrapper>
        <Card icon={<XCircle className="w-8 h-8 text-red-400" />} title="Missing check-in code">
          <p>The QR you scanned is missing its token. Ask your stage manager to reshow the QR.</p>
        </Card>
      </Wrapper>
    );
  }

  if (user === null) {
    return (
      <Wrapper>
        <Card icon={<Loader2 className="w-8 h-8 text-gold animate-spin" />} title="Signing you in…">
          <p className="text-zinc-400">One second while we bring up your account.</p>
        </Card>
      </Wrapper>
    );
  }

  const r = state.data?.rehearsal;

  const enterProduction = () => {
    if (r?.production_id) {
      selectProduction(r.production_id);
      nav("/dashboard");
    }
  };

  return (
    <Wrapper>
      {state.status === "loading" && (
        <Card icon={<Loader2 className="w-8 h-8 text-gold animate-spin" />} title={action === "out" ? "Checking you out…" : "Checking you in…"}>
          <p className="text-zinc-400">Marking your attendance.</p>
        </Card>
      )}

      {state.status === "success" && (
        <Card
          icon={<CheckCircle2 className="w-10 h-10 text-emerald-400" />}
          title={action === "out" ? "You're checked out" : "You're checked in"}
        >
          {r && (
            <div className="mt-2 space-y-1">
              <div className="font-display text-xl text-white">{r.title}</div>
              <div className="text-sm text-zinc-400">
                {fmtDate(r.date)} · {fmtTimeRange(r.start_time, r.end_time)}
              </div>
              {r.location && <div className="text-sm text-zinc-500">📍 {r.location}</div>}
            </div>
          )}
          <div className="mt-6 space-y-2">
            {action === "in" ? (
              <button
                data-testid="checkout-btn"
                onClick={() => submit("out")}
                className="w-full h-11 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" /> Check out when you leave
              </button>
            ) : (
              <button
                data-testid="checkin-again-btn"
                onClick={() => submit("in")}
                className="w-full h-11 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" /> Check back in
              </button>
            )}
            <button
              data-testid="enter-production-btn"
              onClick={enterProduction}
              style={goldBtn}
              className="w-full h-11 rounded font-semibold text-sm"
            >
              Open production desk
            </button>
          </div>
        </Card>
      )}

      {state.status === "error" && (
        <Card icon={<XCircle className="w-8 h-8 text-red-400" />} title="Couldn't check you in">
          <p className="text-red-300 text-sm" data-testid="checkin-error">{state.error}</p>
          <div className="mt-4 space-y-2">
            <button
              onClick={() => submit("in")}
              className="w-full h-10 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5"
            >
              Try again
            </button>
            <Link
              to="/productions"
              className="block text-center text-xs text-zinc-500 hover:text-white"
            >
              Back to productions
            </Link>
          </div>
        </Card>
      )}
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <header className="border-b border-white/10">
        <div className="max-w-md mx-auto px-6 py-5 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" />
          <div>
            <div className="font-display text-lg leading-none">StageCall</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mt-1">Check-in</div>
          </div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-8 text-center shadow-[0_30px_80px_-30px_rgba(245,158,11,0.35)]">
      <div className="mx-auto grid place-items-center">{icon}</div>
      <h1 className="mt-4 font-display text-2xl tracking-tight" data-testid="checkin-title">{title}</h1>
      <div className="mt-3 text-sm text-zinc-300">{children}</div>
    </div>
  );
}
